import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, apiUrl } from '../api/client';
import { Modal, FormError } from './admin-ui';
import type { AssetImportSummary } from '../api/types';
import { ImportMappingPanel, type ImportAnalysis, type ColumnChoice, type ValueChoice } from './asset-import-mapping';

/**
 * Slice 9.7 — Asset CSV Import Wizard (production-ready revision).
 *
 * Three-step flow:
 *   Step 1 — Upload file (no raw CSV textarea shown to user)
 *   Step 2 — Preflight validation report (per-row pass/fail, no raw data)
 *   Step 3 — Done (success summary with counts)
 *
 * Design principles:
 *   - User uploads a file. System processes it. Results are shown cleanly.
 *   - Raw CSV content is NEVER displayed back to the user.
 *   - Column reference is available on demand via a collapsed details element.
 *   - All validation feedback uses structured row-level error messages.
 */

export type MergeStrategy = 'append' | 'upsert' | 'replace';

interface WizardProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}


const ALL_COLUMNS: Array<{ key: string; label: string; required?: boolean; hint?: string }> = [
  { key: 'name',                           label: 'Name',                    required: true },
  { key: 'asset_type_name',                label: 'Asset type',              required: true, hint: 'e.g. Database, Application, API Gateway' },
  { key: 'system_name',                    label: 'System',                  required: true, hint: 'Must match a System in this project scope' },
  { key: 'external_ref',                   label: 'External ref',            hint: 'Your own ID — used for deduplication in Append/Upsert modes' },
  { key: 'description',                    label: 'Description' },
  { key: 'business_criticality',           label: 'Business criticality',    hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'external_exposure',              label: 'External exposure',       hint: 'None | Internal_only | Partner | Internet_facing | Air_gapped | Unknown' },
  { key: 'lifecycle_status',               label: 'Lifecycle status',        hint: 'Planning | Development | Testing | Active | Maintenance | Deprecated | Retired' },
  // Unified platform contract columns (Asset Unification) — optional;
  // when omitted, status/exposure derive from lifecycle/external exposure.
  { key: 'status',                         label: 'Status (unified)',        hint: 'PLANNING | DEVELOPMENT | TESTING | ACTIVE | INACTIVE | MAINTENANCE | DEPRECATED | RETIRED' },
  { key: 'environment',                    label: 'Environment (unified)',   hint: 'PRODUCTION | DEVELOPMENT | TESTING | UAT | DR | SANDBOX' },
  { key: 'exposure',                       label: 'Exposure (unified)',      hint: 'INTERNET | EXTERNAL | PARTNER | INTERNAL | PRIVATE | ISOLATED' },
  { key: 'crypto_agility',                 label: 'Crypto agility',          hint: 'L0_Initial | L1_Developing | L2_Defined | L3_Managed | L4_Optimized' },
  { key: 'expected_shelf_life',            label: 'Shelf life (years)',      hint: 'Integer, e.g. 7' },
  { key: 'time_for_migration_months',      label: 'Migration time (months)', hint: 'Number of months, e.g. 6' },
  { key: 'time_for_migration',             label: 'Migration time (years)',  hint: 'Only for a register that records years, e.g. 0.5' },
  { key: 'data_security_lifetime',         label: 'Data must stay protected (years)', hint: 'Integer, e.g. 10 — how long the data or signatures must stay secure; leave empty to use shelf life' },
  { key: 'migration_start_date',           label: 'Migration start date',    hint: 'YYYY-MM-DD — when work on this asset starts or started' },
  { key: 'upgradability',                  label: 'Upgradability',           hint: 'Very_easy | Easy | Moderate | Difficult | Very_difficult' },
  { key: 'legacy_system',                  label: 'Legacy system',           hint: 'true | false' },
  { key: 'hardware_resource_constraints',  label: 'Hardware constraints',    hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'certification_requirement',      label: 'Certification required',  hint: 'true | false' },
  { key: 'source_code_availability',       label: 'Source code available',   hint: 'true | false' },
  { key: 'risk_tolerance',                 label: 'Risk tolerance',          hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'data_part_of',                   label: 'Domain',                  hint: 'IT | OT | Product | Embedded | Unknown' },
  { key: 'owner_role_text',                label: 'Owner / role' },
  { key: 'location',                       label: 'Location',                hint: 'e.g. London DC, AWS eu-west-2' },
  { key: 'usage_start_date',               label: 'Usage start date',        hint: 'YYYY-MM-DD' },
  { key: 'end_of_life_date',               label: 'End of life date',        hint: 'YYYY-MM-DD' },
  { key: 'is_confidential',               label: 'Confidential',            hint: 'true | false' },
  // Data risk and migration inputs
  { key: 'data_sensitivity',               label: 'Data sensitivity',        hint: 'Very_low | Low | Moderate | High | Very_high — drives Data risk and the lifetime test' },
  { key: 'sndl_threat_exposure',           label: 'Harvest-now-decrypt-later (HNDL) exposure', hint: 'Very_low | Low | Moderate | High | Very_high — chance the data is being recorded now to decrypt later' },
  { key: 'regulatory_requirements_level',  label: 'Regulatory requirements', hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'migration_cost_range',           label: 'Migration cost range',    hint: 'Amounts in the project currency: Range_under_10000 | Range_10000_25000 | Range_25000_50000 | Range_50000_100000 | Range_100000_250000 | Range_250000_500000 | Range_500000_1000000 | Range_1000000_2500000 | Range_2500000_5000000 | Range_5000000_10000000 | Range_above_10000000 (the five older bands are still accepted)' },
  { key: 'migration_cost_amount',          label: 'Migration cost amount',   hint: 'An exact amount in the project currency, e.g. 350000: kept as given and placed in its band' },
  { key: 'complexity',                     label: 'Migration complexity',    hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'technical_operational_feasibility', label: 'Feasibility',          hint: 'Very_low | Low | Moderate | High | Very_high' },
  // Supply chain inputs
  { key: 'vendor_supplied_asset',          label: 'Vendor supplied',         hint: 'Yes | No' },
  { key: 'vendor_name',                    label: 'Vendor name',             hint: 'e.g. Oracle — matched to vendor commitments in the reporting profile' },
  { key: 'vendor_readiness',               label: 'Vendor readiness',        hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'value_of_asset',                 label: 'Value of asset',          hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'alternative_sourcing_difficulty', label: 'Alternative sourcing difficulty', hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'active_engagement',              label: 'Vendor engagement',       hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'procurement_timelines_cycles',   label: 'Procurement timelines',   hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'source_country_risk',            label: 'Source country risk',     hint: 'Very_low | Low | Moderate | High | Very_high' },
  { key: 'qram_asset_id',                  label: 'QRAM asset id',           hint: 'Written by Export; keeps updates on the same asset. Blank for new assets' },
];

const MERGE_STRATEGY_DESCRIPTIONS: Record<MergeStrategy, { label: string; description: string; color: string }> = {
  append:  { label: 'Append',          description: 'Add new assets. Rows matching an asset already in the project (by qram_asset_id or external_ref) are skipped.', color: 'text-green-700' },
  upsert:  { label: 'Update or insert', description: 'Update assets matched by qram_asset_id or external_ref, changing only the cells you filled in (empty cells and missing columns keep their values); add the rest as new.', color: 'text-blue-700' },
  replace: { label: 'Replace',         description: 'Remove the previously imported assets from this project, then load the file. Rows matching an asset entered by hand update it instead of duplicating it.', color: 'text-amber-700' },
};

// Parse just the header row to detect column names — used internally for
// validation only, never shown to the user as raw data.
// ============================================================================
// Main component
// ============================================================================

export function AssetImportWizard({ open, onClose, projectId }: WizardProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [strategy, setStrategy] = useState<MergeStrategy>('append');
  const [preflightResult, setPreflightResult] = useState<AssetImportSummary | null>(null);
  const [finalResult, setFinalResult] = useState<AssetImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  // System for rows with no system_name (a CMDB export without a system column, such as ServiceNow's).
  const [defaultSystem, setDefaultSystem] = useState('');
  // Mapping step: the file's columns and values beside QRAM's (saved with the project on Validate).
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  const [columns, setColumns] = useState<ColumnChoice>({});
  const [values, setValues] = useState<ValueChoice>({});
  const [newSystemsBu, setNewSystemsBu] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const hasFile = csvText.trim().length > 0;
  const mappedFields = new Set(Object.values(columns).filter(Boolean));
  const noSystemColumn = !mappedFields.has('system_name');
  const missingRequired = [
    ...(mappedFields.has('name') || mappedFields.has('hostname') ? [] : ['name (or host name)']),
    ...(mappedFields.has('asset_type_name') ? [] : ['asset type']),
    ...(!noSystemColumn || defaultSystem ? [] : ['system']),
  ];

  const analyze = useCallback(async (csv: string, cols: ColumnChoice | null) => {
    try {
      const res = await api.post<ImportAnalysis>(`/projects/${projectId}/assets/import/analyze`, { csv, columns: cols });
      setAnalysis(res);
      setColumns(Object.fromEntries(res.columns.map((c) => [c.header, c.field ?? ''])));
      // One business unit in scope: new systems go there unless the user says otherwise.
      if (res.business_units.length === 1) setNewSystemsBu((v) => v || res.business_units[0]!.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read the file\'s columns.');
    }
  }, [projectId]);
  useEffect(() => { if (csvText.trim()) void analyze(csvText, null); else setAnalysis(null); }, [csvText, analyze]);
  const onColumn = (header: string, field: string) => { const next = { ...columns, [header]: field }; setColumns(next); void analyze(csvText, next); };
  const onValue = (field: string, value: string, mapped: string) => setValues((v) => ({ ...v, [field]: { ...(v[field] ?? {}), [value]: mapped } }));
  const mappedBody = () => ({ csv: csvText, columns, values, system: defaultSystem || null, new_systems_bu: noSystemColumn ? null : newSystemsBu || null });

  // Fetch the systems available in this project's scope so the user
  // knows which system_name values are valid in their CSV.
  const { data: scopedSystems = [], isSuccess: systemsLoaded } = useQuery({
    queryKey: ['project-systems', projectId],
    queryFn: () => api.get<Array<{ id: string; name: string; short_code: string; bu_name: string }>>(
      `/projects/${projectId}/systems`,
    ),
    staleTime: 60_000,
  });
  // One system in scope: rows with no system go there unless the user says otherwise.
  const onlySystem = scopedSystems.length === 1 ? scopedSystems[0]!.short_code || scopedSystems[0]!.name : '';
  useEffect(() => { if (onlySystem && analysis) setDefaultSystem((v) => v || onlySystem); }, [onlySystem, analysis?.columns.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const preflightMutation = useMutation({
    mutationFn: () =>
      api.post<AssetImportSummary>(`/projects/${projectId}/assets/import/mapped?mode=preflight`, mappedBody()),
    onSuccess: (data) => { setPreflightResult(data); setError(null); setStep(2); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Validation failed. Check your file and try again.'),
  });

  const commitMutation = useMutation({
    mutationFn: () =>
      api.post<AssetImportSummary>(`/projects/${projectId}/assets/import/mapped?mode=${strategy}`, mappedBody()),
    onSuccess: (data) => {
      setFinalResult(data);
      setError(null);
      setStep(3);
      qc.invalidateQueries({ queryKey: ['project-assets-detailed', projectId] });
      qc.invalidateQueries({ queryKey: ['projects', projectId] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Import failed. Please try again.'),
  });

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError(null);
    if (/\.xlsx$/i.test(file.name)) {
      // An Excel CMDB export (e.g. ServiceNow's Export → Excel): the server reads the first sheet and
      // returns it as CSV; the mapping step then matches its columns.
      const xr = new FileReader();
      xr.onload = async (ev) => {
        try {
          const data = String(ev.target?.result ?? '').replace(/^data:[^,]*,/, '');
          const res = await api.post<{ csv: string }>(`/projects/${projectId}/assets/import/xlsx`, { file_name: file.name, data });
          setCsvText(res.csv);
        } catch (err) {
          setFileName(null);
          setError(err instanceof ApiError ? err.message : 'Failed to read the workbook.');
        }
      };
      xr.onerror = () => setError('Failed to read file.');
      xr.readAsDataURL(file);
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target?.result as string ?? '');
    reader.onerror = () => setError('Failed to read file. Ensure it is a valid CSV.');
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  }, [projectId]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const res = await fetch(apiUrl(`/projects/${projectId}/assets/import/template`), { credentials: 'same-origin' });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'qram-asset-import-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template.');
    }
  }, [projectId]);

  function handleClose() {
    setStep(1); setCsvText(''); setFileName(null); setAnalysis(null); setColumns({}); setValues({}); setDefaultSystem(''); setNewSystemsBu('');
    setPreflightResult(null); setFinalResult(null); setError(null);
    onClose();
  }

  const titleByStep: Record<1 | 2 | 3, string> = {
    1: 'Import assets — Step 1: Upload',
    2: 'Import assets — Step 2: Validation report',
    3: 'Import assets — Done',
  };

  return (
    <Modal open={open} onClose={handleClose} title={titleByStep[step]} widthClass="max-w-5xl">
      <div className="space-y-5">

        {/* Progress indicator */}
        <div className="flex items-center gap-2 text-xs text-ink-500">
          {(['Upload', 'Validate', 'Commit'] as const).map((label, i) => (
            <span key={label} className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium ${
                step === i + 1 ? 'bg-accent text-white' : step > i + 1 ? 'bg-green-100 text-green-700' : 'bg-ink-100 text-ink-400'
              }`}>{i + 1}</span>
              <span className={step === i + 1 ? 'text-ink font-medium' : ''}>{label}</span>
              {i < 2 && <span className="text-ink-200">→</span>}
            </span>
          ))}
        </div>

        {/* ── STEP 1: Upload ── */}
        {step === 1 && (
          <>
            {/* Merge strategy */}
            <div>
              <div className="text-sm font-medium text-ink mb-2">Merge strategy</div>
              <div className="grid grid-cols-3 gap-2">
                {(Object.entries(MERGE_STRATEGY_DESCRIPTIONS) as [MergeStrategy, typeof MERGE_STRATEGY_DESCRIPTIONS[MergeStrategy]][]).map(
                  ([key, { label, description, color }]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setStrategy(key)}
                      className={`text-left p-3 rounded border text-sm transition-colors ${
                        strategy === key ? 'border-accent bg-accent/5' : 'border-ink-200 hover:border-ink-400 bg-white'
                      }`}
                    >
                      <div className={`font-medium mb-1 ${strategy === key ? 'text-accent' : color}`}>{label}</div>
                      <div className="text-xs text-ink-500 leading-snug">{description}</div>
                    </button>
                  ),
                )}
              </div>
            </div>

            {/* File upload area — NO raw textarea shown */}
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-sm font-medium text-ink">CSV file</div>
                <button type="button" onClick={handleDownloadTemplate} className="text-xs text-accent hover:underline">
                  Download template
                </button>
              </div>

              {/* Drop zone / upload button */}
              <div
                className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
                  hasFile ? 'border-green-300 bg-green-50' : 'border-ink-200 bg-ink-50 hover:border-accent'
                }`}
              >
                {hasFile ? (
                  <div className="space-y-2">
                    <div className="text-2xl">✓</div>
                    <div className="text-sm font-medium text-green-700">{fileName}</div>
                    <div className="text-xs text-ink-500">
                      {analysis ? `${analysis.columns.length} columns, ${analysis.rows} rows` : 'Reading columns…'}
                      {missingRequired.length > 0 && (
                        <span className="ml-2 text-risk-critical font-medium">
                          — missing required: {missingRequired.join(', ')}
                        </span>
                      )}
                      {missingRequired.length === 0 && (
                        <span className="ml-2 text-green-600"> — all required columns present</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCsvText(''); setFileName(null); setError(null); setDefaultSystem(''); setAnalysis(null); setColumns({}); setValues({}); setNewSystemsBu(''); }}
                      className="text-xs text-ink-400 hover:text-ink-700 underline mt-1"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-3xl text-ink-300">↑</div>
                    <div className="text-sm text-ink-500">
                      Click to select a CSV or Excel (.xlsx) file
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover"
                    >
                      Choose file…
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
            </div>

            {hasFile && analysis && (
              <ImportMappingPanel analysis={analysis} columns={columns} onColumn={onColumn} values={values} onValue={onValue}
                systems={scopedSystems} defaultSystem={defaultSystem} onDefaultSystem={setDefaultSystem}
                newSystemsBu={newSystemsBu} onNewSystemsBu={setNewSystemsBu} />
            )}

            {/* Systems in scope — CRITICAL: user must know these before writing CSV */}
            {systemsLoaded && scopedSystems.length === 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-xs text-amber-800" data-testid="no-systems">
                This project has no systems yet, so every row would fail. Create the business units and systems first,
                in this project's Settings → Business units and systems.
              </div>
            )}
            {!hasFile && scopedSystems.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-sm p-3">
                <div className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wider">
                  ⚠ Valid system_name values for this project
                </div>
                <p className="text-xs text-amber-700 mb-2">
                  The <code className="font-mono bg-amber-100 px-1">system_name</code> column in your CSV must exactly match one of these system names (case-insensitive):
                </p>
                <div className="flex flex-wrap gap-2">
                  {scopedSystems.map((s) => (
                    <div key={s.id} className="bg-white border border-amber-200 rounded px-2 py-1">
                      <span className="font-mono text-xs font-semibold text-amber-900">{s.name}</span>
                      <span className="text-[10px] text-amber-600 ml-1">({s.bu_name})</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-amber-600 mt-2">
                  After you choose a file, the next screen lets you take the system from any column, and create the systems the project does not have yet.
                </p>
              </div>
            )}

            {/* Column reference — collapsed by default */}
            <details className="text-xs text-ink-500">
              <summary className="cursor-pointer font-medium">▶ Column reference</summary>
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 mt-2">
                {ALL_COLUMNS.map((col) => (
                  <div key={col.key} className="flex gap-1.5">
                    <span className={`font-mono shrink-0 ${col.required ? 'text-accent font-medium' : 'text-ink-600'}`}>
                      {col.key}{col.required ? ' *' : ''}
                    </span>
                    {col.hint && <span className="text-ink-400 truncate">— {col.hint}</span>}
                  </div>
                ))}
              </div>
              <p className="mt-2 text-ink-400">* Required. All other columns are optional.</p>
            </details>

            <FormError message={error} />

            <div className="flex justify-between items-center pt-2 border-t border-ink-100">
              <button type="button" onClick={handleClose} className="text-sm text-ink-500 hover:text-ink">
                Cancel
              </button>
              <button
                type="button"
                disabled={!hasFile || missingRequired.length > 0 || !analysis || preflightMutation.isPending}
                onClick={() => preflightMutation.mutate()}
                className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover disabled:opacity-50"
              >
                {preflightMutation.isPending ? 'Validating…' : 'Validate CSV →'}
              </button>
            </div>
          </>
        )}

        {/* ── STEP 2: Preflight results ── */}
        {step === 2 && preflightResult && (
          <>
            <PreflightResultDisplay summary={preflightResult} strategy={strategy} />
            <FormError message={error} />
            <div className="flex justify-between items-center pt-2 border-t border-ink-100">
              <button
                type="button"
                onClick={() => { setStep(1); setError(null); }}
                className="text-sm text-ink-500 hover:text-ink"
              >
                ← Edit file
              </button>
              <button
                type="button"
                disabled={preflightResult.failed > 0 || commitMutation.isPending}
                onClick={() => commitMutation.mutate()}
                className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover disabled:opacity-50"
                title={preflightResult.failed > 0 ? 'Fix failed rows before importing' : ''}
              >
                {commitMutation.isPending
                  ? 'Importing…'
                  : preflightResult.failed > 0
                    ? `Cannot import — ${preflightResult.failed} error(s)`
                    : `Import ${preflightResult.imported} asset${preflightResult.imported !== 1 ? 's' : ''} (${MERGE_STRATEGY_DESCRIPTIONS[strategy].label})`}
              </button>
            </div>
          </>
        )}

        {/* ── STEP 3: Final result ── */}
        {step === 3 && finalResult && (
          <>
            <FinalResultDisplay summary={finalResult} />
            <div className="flex justify-end pt-2 border-t border-ink-100">
              <button
                type="button"
                onClick={handleClose}
                className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============================================================================
// Preflight result display — no raw data shown
// ============================================================================

function PreflightResultDisplay({ summary, strategy }: { summary: AssetImportSummary; strategy: MergeStrategy }) {
  const allPassed = summary.failed === 0;
  const strategyInfo = MERGE_STRATEGY_DESCRIPTIONS[strategy];

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-ink-50 rounded p-3">
          <div className="text-xs text-ink-500">Total rows</div>
          <div className="text-xl font-mono mt-0.5">{summary.total_rows}</div>
        </div>
        <div className={`rounded p-3 ${allPassed ? 'bg-green-50' : 'bg-ink-50'}`}>
          <div className="text-xs text-ink-500">Valid</div>
          <div className={`text-xl font-mono mt-0.5 ${allPassed ? 'text-green-700' : ''}`}>{summary.imported}</div>
        </div>
        <div className={`rounded p-3 ${summary.failed > 0 ? 'bg-red-50' : 'bg-ink-50'}`}>
          <div className="text-xs text-ink-500">Failed</div>
          <div className={`text-xl font-mono mt-0.5 ${summary.failed > 0 ? 'text-risk-critical' : ''}`}>{summary.failed}</div>
        </div>
        <div className="bg-ink-50 rounded p-3">
          <div className="text-xs text-ink-500">Mode</div>
          <div className={`text-sm font-medium mt-0.5 ${strategyInfo.color}`}>{strategyInfo.label}</div>
        </div>
      </div>

      {/* Outcome message */}
      {allPassed ? (
        <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
          All {summary.total_rows} rows passed validation. Click <strong>Import</strong> to commit.
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-risk-critical">
          {summary.failed} row{summary.failed !== 1 ? 's' : ''} failed validation — fix your file and re-upload.
        </div>
      )}

      <ImportColumnsDisplay summary={summary} />

      {/* Parse-level errors */}
      {summary.parse_errors.length > 0 && (
        <div className="border border-red-200 rounded bg-red-50 p-3">
          <div className="text-xs font-medium text-risk-critical mb-2">{summary.parse_errors.length} CSV parse error(s)</div>
          <ul className="space-y-1 text-xs font-mono text-risk-critical">
            {summary.parse_errors.slice(0, 10).map((e, i) => (
              <li key={i}>Row {e.row_num}: {e.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Row-level results — errors only if there are failures; otherwise summary */}
      {summary.failed > 0 ? (
        <div className="border border-ink-200 rounded overflow-hidden">
          <div className="bg-red-50 border-b border-red-100 px-3 py-2 text-xs font-medium text-risk-critical uppercase tracking-wider">
            Rows with errors
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white border-b border-ink-100">
                <tr>
                  <th className="text-left px-3 py-2 text-ink-400 font-medium w-12">Row</th>
                  <th className="text-left px-3 py-2 text-ink-400 font-medium">Asset name</th>
                  <th className="text-left px-3 py-2 text-ink-400 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {summary.results.filter((r) => r.status === 'failed').map((r) => (
                  <tr key={r.row_num} className="border-b border-red-50 bg-red-50/60">
                    <td className="px-3 py-1.5 font-mono text-ink-400">{r.row_num}</td>
                    <td className="px-3 py-1.5 text-ink">{r.name ?? '—'}</td>
                    <td className="px-3 py-1.5 text-risk-critical">{r.errors?.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="border border-green-200 rounded bg-green-50 px-3 py-2 text-xs text-green-700">
          All rows validated successfully. Assets will be created with all uploaded field values.
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Final result display — clean summary, no raw data
// ============================================================================

function FinalResultDisplay({ summary }: { summary: AssetImportSummary }) {
  const totalSuccess = summary.imported + summary.updated;
  return (
    <div className="space-y-4">
      <div className={`rounded p-4 border ${
        summary.failed === 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="font-medium text-sm mb-1">
          {summary.failed === 0
            ? `Import complete — ${totalSuccess} asset${totalSuccess !== 1 ? 's' : ''} processed`
            : `Import finished with ${summary.failed} error${summary.failed !== 1 ? 's' : ''}`}
        </div>
        <div className="text-xs text-ink-500 space-y-0.5">
          <div>Batch ID: <span className="font-mono">{summary.import_batch_id ?? '—'}</span></div>
          <div>The Assets tab has been refreshed. Open any asset to review and edit imported values.</div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total rows', value: summary.total_rows, cls: 'bg-ink-50' },
          { label: 'Inserted',   value: summary.imported,   cls: 'bg-green-50 text-green-700' },
          { label: 'Updated',    value: summary.updated,    cls: 'bg-blue-50 text-blue-700' },
          { label: 'Failed',     value: summary.failed,     cls: summary.failed > 0 ? 'bg-red-50 text-risk-critical' : 'bg-ink-50' },
        ].map(({ label, value, cls }) => (
          <div key={label} className={`rounded p-3 ${cls.split(' ')[0]}`}>
            <div className="text-xs text-ink-500">{label}</div>
            <div className={`text-xl font-mono mt-0.5 ${cls.split(' ').slice(1).join(' ')}`}>{value}</div>
          </div>
        ))}
      </div>

      <ImportColumnsDisplay summary={summary} />

      {summary.skipped > 0 && (
        <div className="text-xs text-ink-500 bg-ink-50 rounded p-3">
          {summary.skipped} row{summary.skipped !== 1 ? 's' : ''} skipped (already enrolled with same external_ref in Append mode).
        </div>
      )}

      {summary.failed > 0 && (
        <div className="border border-red-200 rounded bg-red-50 p-3">
          <div className="text-xs font-medium text-risk-critical mb-2">
            {summary.failed} row{summary.failed !== 1 ? 's' : ''} were NOT imported
          </div>
          <ul className="space-y-0.5 text-xs font-mono text-risk-critical">
            {summary.results.filter((r) => r.status === 'failed').slice(0, 50).map((r) => (
              <li key={r.row_num}>Sheet row {r.row_num} ({r.name ?? '?'}): {r.errors?.join('; ')}</li>
            ))}
          </ul>
          {summary.failed > 50 && <div className="text-xs text-risk-critical mt-1">and {summary.failed - 50} more.</div>}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Columns read and left out, and row warnings: nothing in the file is dropped silently
// ============================================================================

export function ImportColumnsDisplay({ summary }: { summary: AssetImportSummary }) {
  const mapped = summary.columns?.mapped ?? [];
  const skipped = summary.columns?.skipped ?? [];
  const warned = summary.results.filter((r) => (r.warnings?.length ?? 0) > 0);
  if (!summary.columns && warned.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="import-columns">
      {summary.columns && (
        <div className="border border-ink-200 rounded p-3 text-xs">
          <div className="font-medium text-ink-700 mb-1">
            Columns: {mapped.length} imported{skipped.length > 0 ? `, ${skipped.length} not imported` : ''}
          </div>
          {mapped.length > 0 && (
            <div className="text-ink-500">
              {mapped.map((m) => (m.header === m.field ? m.header : `${m.header} → ${m.field}`)).join(', ')}
            </div>
          )}
          {skipped.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-amber-800">
              {skipped.map((c) => (
                <li key={c.header}>Skipped <span className="font-mono">{c.header}</span>: {c.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {warned.length > 0 && (
        <div className="border border-amber-200 rounded bg-amber-50 p-3 text-xs text-amber-800">
          <div className="font-medium mb-1">{warned.length} row{warned.length !== 1 ? 's' : ''} with warnings</div>
          <ul className="space-y-0.5 max-h-40 overflow-y-auto">
            {warned.slice(0, 50).map((r) => (
              <li key={r.row_num}>Sheet row {r.row_num} ({r.name ?? '?'}): {r.warnings!.join('; ')}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
